/**
 * The activities of one screen: the ordered list, and the five things you can do to one.
 *
 * Every mutation goes through the editor store (B9). The store's moves are already safe no-ops at
 * the edges, so disabling the edge buttons is purely an affordance, exactly as the rail does.
 *
 * The screen id arrives as a GETTER — a snapshot would keep pointing at the previous screen.
 */
import { computed } from 'vue'

import type { ComputedRef } from 'vue'
import type { Activity } from './interfaces'

import { withCount } from '@shared/lib/format'

import { useAssessmentEditorStore } from './store'
import { getActivityKindLabel } from './interaction-labels'

/** How much of the first text block the list shows — enough to tell two activities apart, not
 *  enough to wrap. Same figure as the screen rail. */
const SUMMARY_LENGTH = 40

interface ActivityListItem {
  id: string
  /** 1-based, because that is how an author counts activities. */
  position: number
  /** The row's title: `Activity 1 | Multi-digit addition | Multiple choice`. The name is left out
   *  when the activity has none, since an empty segment reads as a missing field. */
  label: string
  /** The row's second line: the truncated first text block, plus points when set — `points` is
   *  optional, so the segment is dropped rather than shown as zero. */
  metaLabel: string
  /** The interaction's human name, "Tutor observation" for a prompt-only activity with rubrics, or
   *  "No interaction" for one without. */
  interactionLabel: string
  isSelected: boolean
  isFirstInOrder: boolean
  isLastInOrder: boolean
}

export interface ActivityListProps {
  screenId: string
}

interface UseActivityListOptions {
  screenId: () => string
}

interface UseActivityListReturn {
  refActivities: ComputedRef<ActivityListItem[]>
  refHasActivities: ComputedRef<boolean>
  refSelectedActivityId: ComputedRef<string | null>
  selectActivity: (activityId: string) => void
  addActivity: () => void
  removeActivity: (activityId: string) => void
  moveActivityUp: (activityId: string) => void
  moveActivityDown: (activityId: string) => void
}

/**
 * An `Activity` has no `title` in the schema, and inventing one would be a document change this
 * task may not make. So an activity is identified by what the document already holds: its
 * position, the truncated first text block, the interaction type, and `points` when set.
 */
const summarise = (activity: Activity): string => {
  const text = activity.content.find(block => block.type === 'text')
  const value = text?.type === 'text' ? text.value.trim() : ''
  if (!value) {
    return activity.content.length ? 'No text yet' : 'No content yet'
  }
  return value.length > SUMMARY_LENGTH ? `${value.slice(0, SUMMARY_LENGTH)}…` : value
}

const metaLabel = (activity: Activity): string =>
  [
    summarise(activity),
    activity.points == null ? '' : withCount(activity.points, { one: 'point', other: 'points' }),
  ].filter(Boolean).join(' · ')

/**
 * `Activity 1 | Multi-digit addition | Multiple choice` — position, the author's own name for the
 * question, and what the kid does with it. The name segment is dropped when there is none.
 */
const label = (activity: Activity, position: number, kindLabel: string): string =>
  [
    `Activity ${position}`,
    activity.name?.trim() || '',
    kindLabel,
  ].filter(Boolean).join(' | ')

export const useActivityList = ({ screenId }: UseActivityListOptions): UseActivityListReturn => {
  const store = useAssessmentEditorStore()

  const refScreenActivities = computed<Activity[]>(() =>
    store.getScreens.find(screen => screen.id === screenId())?.activities ?? [],
  )

  const refActivities = computed<ActivityListItem[]>(() => {
    const activities = refScreenActivities.value
    return activities.map((activity, index) => {
      // The kind is the biggest difference between two activities, so it is part of the row's
      // title rather than a line under it.
      const kindLabel = getActivityKindLabel(activity, store.getRubricCatalog)

      return {
        id: activity.id,
        position: index + 1,
        label: label(activity, index + 1, kindLabel),
        metaLabel: metaLabel(activity),
        interactionLabel: kindLabel,
        isSelected: activity.id === store.getSelectedActivityId,
        isFirstInOrder: index === 0,
        isLastInOrder: index === activities.length - 1,
      }
    })
  })

  const refHasActivities = computed<boolean>(() => refScreenActivities.value.length > 0)

  const refSelectedActivityId = computed<string | null>(() => store.getSelectedActivityId)

  /** A TOGGLE, not a select: clicking the open card's title closes it again. The store's selection
   *  is what the card's `v-if` reads, so deselecting IS collapsing — there is no second piece of
   *  open/closed state to keep in step. */
  const selectActivity = (activityId: string): void => {
    store.selectActivity(
      store.getSelectedActivityId === activityId ? null : activityId,
    )
  }

  /** The store selects the new activity itself and returns its id. */
  const addActivity = (): void => {
    store.addActivity(screenId())
  }

  const removeActivity = (activityId: string): void => {
    store.removeActivity(screenId(), activityId)
  }

  const moveActivityUp = (activityId: string): void => {
    store.moveActivityUp(screenId(), activityId)
  }

  const moveActivityDown = (activityId: string): void => {
    store.moveActivityDown(screenId(), activityId)
  }

  return {
    refActivities,
    refHasActivities,
    refSelectedActivityId,
    selectActivity,
    addActivity,
    removeActivity,
    moveActivityUp,
    moveActivityDown,
  }
}
